import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { listWhatsAppFlowSelector } from "../../../api/whatsAppFlows";
import { getCustomFieldDefinitions } from "../../CustomFields/customFieldsApi";
import {
  isWhatsAppFlowFieldCompatible,
  normalizeWhatsAppFlowConfig,
  validateWhatsAppFlowConfig,
  WHATSAPP_FLOW_DESTINATION_KINDS,
} from "../utils/whatsAppFlowValidation";
import AutoReplyFollowUpEditor, {
  emptyFollowUp,
  validateFollowUp,
} from "./AutoReplyFollowUpEditor";

const sectionClass = "space-y-3 rounded-lg border border-slate-200 bg-white p-4";
const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500";

export default function WhatsAppFlowNodeEditor({ node, onClose, onSave, templates = [] }) {
  const [config, setConfig] = useState(() =>
    normalizeWhatsAppFlowConfig(node?.data?.config || {})
  );
  const [flows, setFlows] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [requestContextFields, setRequestContextFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [validation, setValidation] = useState(null);
  const [followUp, setFollowUp] = useState(() => node?.data?.followUp || emptyFollowUp());

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listWhatsAppFlowSelector(),
      getCustomFieldDefinitions({ entityType: "Contact", includeInactive: true }),
      getCustomFieldDefinitions({ entityType: "REQUEST_CONTEXT", includeInactive: true }),
    ])
      .then(([flowItems, fields, contextFields]) => {
        if (cancelled) return;
        setFlows(Array.isArray(flowItems) ? flowItems : []);
        setCustomFields(Array.isArray(fields) ? fields : []);
        setRequestContextFields(Array.isArray(contextFields) ? contextFields : []);
      })
      .catch(error => {
        if (!cancelled) {
          setLoadError(
            error?.status === 403
              ? "You no longer have permission to browse imported WhatsApp Forms."
              : "Imported WhatsApp Forms and destination fields could not be loaded."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedFlow = useMemo(
    () =>
      flows.find(
        flow =>
          String(flow.definitionId) === String(config.flowDefinitionId) &&
          String(flow.revisionId) === String(config.flowRevisionId)
      ) || null,
    [config.flowDefinitionId, config.flowRevisionId, flows]
  );
  const selectedFields = selectedFlow?.fields || [];
  const staleSelection =
    !loading &&
    !!config.flowDefinitionId &&
    !selectedFlow;

  const selectFlow = value => {
    const nextFlow = flows.find(flow => String(flow.definitionId) === value);
    if (!nextFlow) return;
    if (
      config.fieldMappings.length > 0 &&
      String(config.flowDefinitionId) !== String(nextFlow.definitionId) &&
      !window.confirm(
        "Changing the WhatsApp Form will clear the existing response mappings. Continue?"
      )
    )
      return;
    setConfig(previous => ({
      ...previous,
      flowDefinitionId: nextFlow.definitionId,
      flowRevisionId: nextFlow.revisionId,
      fieldMappings: nextFlow.fields.map(field => ({
        responseKey: field.responseKey,
        responseType: field.responseType,
        fieldId: "",
        destinationKind: WHATSAPP_FLOW_DESTINATION_KINDS.CONTACT_CUSTOM_FIELD,
        required: field.isRequired === true,
      })),
    }));
    setValidation(null);
  };

  const updateMapping = (responseKey, patch) =>
    setConfig(previous => ({
      ...previous,
      fieldMappings: previous.fieldMappings.map(mapping =>
        mapping.responseKey === responseKey ? { ...mapping, ...patch } : mapping
      ),
    }));

  const addInitialBinding = () =>
    setConfig(previous => ({
      ...previous,
      initialDataBindings: [
        ...previous.initialDataBindings,
        {
          dataKey: "",
          sourceKind: "SessionValue",
          sourceFieldKey: "",
          sourceFieldId: null,
          literalValue: null,
          required: false,
        },
      ],
    }));

  const updateInitialBinding = (index, patch) =>
    setConfig(previous => ({
      ...previous,
      initialDataBindings: previous.initialDataBindings.map((binding, bindingIndex) =>
        bindingIndex === index ? { ...binding, ...patch } : binding
      ),
    }));

  const save = () => {
    const result = validateWhatsAppFlowConfig(config, {
      revisionFields: selectedFields,
      customFields: [...customFields, ...requestContextFields],
    });
    setValidation(result);
    if (!result.isValid || staleSelection) return;
    const followUpError = validateFollowUp(
      followUp,
      Number(result.config.timeoutPolicy?.timeoutMinutes) * 60
    );
    if (followUpError) {
      setValidation({ isValid: false, message: followUpError });
      return;
    }
    onSave?.({
      ...node,
      data: {
        ...(node.data || {}),
        config: result.config,
        whatsAppFlowDisplayName: selectedFlow?.name || "",
        followUp,
        followUpEligible: true,
        followUpMaxHorizonSeconds:
          Number(result.config.timeoutPolicy?.timeoutMinutes) * 60,
      },
    });
    onClose?.();
  };

  return (
    <>
      <div className="fixed inset-0 z-[1000] bg-black/10 backdrop-blur-[1px]" onClick={onClose} />
      <div className="fixed inset-0 z-[1001] overflow-y-auto px-4 pb-4 pt-[calc(var(--app-shell-topbar-size,64px)+16px)]">
        <div className="mx-auto flex max-h-[calc(100vh-var(--app-shell-topbar-size,64px)-32px)] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shadow-xl">
          <header className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
            <h2 className="text-[15px] font-bold text-slate-800">Edit Whats Form Node</h2>
            <button type="button" onClick={onClose} aria-label="Close Whats Form editor" className="rounded p-1 text-slate-500 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            <section className={sectionClass}>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Form</h3>
                <p className="text-xs text-slate-500">Select an active imported published Endpointless Form. The revision is pinned.</p>
              </div>
              {loadError && <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">{loadError}</p>}
              {staleSelection && <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">The pinned Form revision is no longer available.</p>}
              <select
                aria-label="Imported WhatsApp Form"
                className={inputClass}
                disabled={loading || !!loadError}
                value={selectedFlow?.definitionId || ""}
                onChange={event => selectFlow(event.target.value)}
              >
                <option value="">{loading ? "Loading Forms…" : "Select a WhatsApp Form"}</option>
                {flows.map(flow => (
                  <option key={flow.definitionId} value={flow.definitionId}>
                    {flow.name} · Revision {flow.revisionNumber} · Endpointless · {flow.fields.length} fields
                  </option>
                ))}
              </select>
            </section>

            <section className={sectionClass}>
              <h3 className="text-sm font-semibold text-slate-800">Entry message</h3>
              <label className="block text-xs font-medium text-slate-700">
                Message body
                <textarea
                  className={`${inputClass} mt-1`}
                  rows={3}
                  maxLength={1024}
                  value={config.entry.bodyText}
                  onChange={event => setConfig(previous => ({ ...previous, entry: { ...previous.entry, bodyText: event.target.value } }))}
                />
              </label>
              <label className="block text-xs font-medium text-slate-700">
                Button text
                <input
                  className={`${inputClass} mt-1`}
                  maxLength={20}
                  value={config.entry.buttonText}
                  onChange={event => setConfig(previous => ({ ...previous, entry: { ...previous.entry, buttonText: event.target.value } }))}
                />
                <span className="mt-1 block text-right text-[11px] text-slate-400">{config.entry.buttonText.length}/20</span>
              </label>
            </section>

            <section className={sectionClass}>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Response mappings</h3>
                <p className="text-xs text-slate-500">Every response must map to a stable Contact or Request Context field.</p>
              </div>
              {selectedFields.length === 0 ? (
                <p className="text-xs text-slate-500">Select a Form to load its normalized response schema.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead><tr className="border-b text-slate-500"><th className="p-2">Response key</th><th className="p-2">Type</th><th className="p-2">Required</th><th className="p-2">Destination</th><th className="p-2">Map to field</th></tr></thead>
                    <tbody>
                      {selectedFields.map(field => {
                        const mapping = config.fieldMappings.find(item => item.responseKey === field.responseKey);
                        const destinationKind = mapping?.destinationKind || WHATSAPP_FLOW_DESTINATION_KINDS.CONTACT_CUSTOM_FIELD;
                        const destinationFields = destinationKind === WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT
                          ? requestContextFields
                          : customFields;
                        const options = destinationFields.filter(destination =>
                          destination.isActive !== false &&
                          isWhatsAppFlowFieldCompatible(field.responseType, destination.dataType, destinationKind)
                        );
                        return (
                          <tr key={field.fieldId} className="border-b border-slate-100">
                            <td className="p-2 font-mono">{field.responseKey}</td>
                            <td className="p-2">{field.responseType}</td>
                            <td className="p-2">{field.isRequired ? "Yes" : "No"}</td>
                            <td className="p-2">
                              <select className={inputClass} aria-label={`Destination ${field.responseKey}`} value={destinationKind} onChange={event => updateMapping(field.responseKey, { destinationKind: event.target.value, fieldId: "" })}>
                                <option value={WHATSAPP_FLOW_DESTINATION_KINDS.CONTACT_CUSTOM_FIELD}>Contact field</option>
                                <option value={WHATSAPP_FLOW_DESTINATION_KINDS.REQUEST_CONTEXT}>Request context</option>
                              </select>
                            </td>
                            <td className="p-2">
                              <select className={inputClass} aria-label={`Map ${field.responseKey}`} value={mapping?.fieldId || ""} onChange={event => updateMapping(field.responseKey, { fieldId: event.target.value })}>
                                <option value="">Select field</option>
                                {options.map(destination => <option key={destination.id} value={destination.id}>{destination.label} ({destination.dataType})</option>)}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className={sectionClass}>
              <div className="flex items-center justify-between">
                <div><h3 className="text-sm font-semibold text-slate-800">Initial data</h3><p className="text-xs text-slate-500">Optional schema-ready values; runtime resolution is enabled later.</p></div>
                <button type="button" onClick={addInitialBinding} className="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs"><Plus className="h-3 w-3" /> Add</button>
              </div>
              {config.initialDataBindings.map((binding, index) => (
                <div key={index} className="grid gap-2 rounded border border-slate-100 p-3 sm:grid-cols-[1fr_1fr_2fr_auto]">
                  <input aria-label={`Initial data key ${index + 1}`} className={inputClass} placeholder="customer_name" value={binding.dataKey} onChange={event => updateInitialBinding(index, { dataKey: event.target.value })} />
                  <select className={inputClass} value={binding.sourceKind} onChange={event => updateInitialBinding(index, { sourceKind: event.target.value, sourceFieldKey: null, sourceFieldId: null, literalValue: null })}>
                    <option value="SessionValue">Session value</option><option value="CanonicalField">Canonical field</option><option value="Literal">Literal</option>
                  </select>
                  {binding.sourceKind === "CanonicalField" ? (
                    <select className={inputClass} value={binding.sourceFieldId || ""} onChange={event => updateInitialBinding(index, { sourceFieldId: event.target.value })}><option value="">Select field</option>{customFields.filter(field => field.isActive !== false).map(field => <option key={field.id} value={field.id}>{field.label}</option>)}</select>
                  ) : (
                    <input className={inputClass} maxLength={binding.sourceKind === "Literal" ? 512 : 200} placeholder={binding.sourceKind === "Literal" ? "Safe literal" : "customer.name"} value={(binding.sourceKind === "Literal" ? binding.literalValue : binding.sourceFieldKey) || ""} onChange={event => updateInitialBinding(index, binding.sourceKind === "Literal" ? { literalValue: event.target.value } : { sourceFieldKey: event.target.value })} />
                  )}
                  <button type="button" aria-label={`Remove initial data binding ${index + 1}`} onClick={() => setConfig(previous => ({ ...previous, initialDataBindings: previous.initialDataBindings.filter((_, itemIndex) => itemIndex !== index) }))} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </section>

            <section className={sectionClass}>
              <h3 className="text-sm font-semibold text-slate-800">Timeout</h3>
              <input aria-label="Timeout minutes" type="number" min={5} max={10080} className={inputClass} value={config.timeoutPolicy.timeoutMinutes} onChange={event => setConfig(previous => ({ ...previous, timeoutPolicy: { timeoutMinutes: Number(event.target.value) } }))} />
              <p className="text-xs text-slate-500">The Timed Out path will be used when the customer does not submit the Form within this period. Runtime timeout processing will be enabled in a later phase.</p>
            </section>

            <AutoReplyFollowUpEditor
              value={followUp}
              onChange={setFollowUp}
              templates={templates}
              maxHorizonSeconds={Number(config.timeoutPolicy.timeoutMinutes) * 60}
              description="Send while this Whats Form is awaiting submission."
            />

            {validation && !validation.isValid && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-700">{validation.message}</p>}
          </div>
          <footer className="flex justify-end gap-2 border-t border-slate-200 bg-white px-5 py-4">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} disabled={loading || !!loadError || staleSelection} className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Save node</button>
          </footer>
        </div>
      </div>
    </>
  );
}
